<template>
  <div class="relative h-full w-full overflow-hidden">
    <!-- API キー未設定時のフォールバック（Map ID 未設定でもマップ自体は出るが、キーが無ければ描画不可） -->
    <div v-if="!apiKey" class="flex h-full w-full items-center justify-center bg-gray5 px-6 text-center font-Spectral text-sm text-gray4">Map view is currently unavailable.</div>
    <div v-else ref="mapContainer" class="h-full w-full"></div>

    <!-- エリアラベル層のスクリム。
         ベースマップ自身が地名（Tokyo / 東京 / Osaka / 大阪 …）を濃く描いており、その上に行き先名を
         置くと二重になって両方読めなくなる。Map ID 指定時は styles での地名非表示が効かず、mapId は
         生成後に変更もできないため、地図の上に背景色の半透明レイヤーを敷いてベースマップごと沈める。
         Figma のモックも実地図を淡くした上にラベルを置いた絵になっている。 -->
    <Transition v-bind="AREA_LAYER_FADE">
      <div v-if="apiKey && isReady && isAreaLayer" class="pointer-events-none absolute inset-0 z-[1] bg-beige1" :style="{ opacity: AREA_LAYER_SCRIM_OPACITY }"></div>
    </Transition>

    <!-- エリアのドット・ラベル。スクリムより上に置くため地図の内部 DOM ではなくここに描く
         （AdvancedMarkerElement だと地図の内側に入り、スクリムがマーカーにも掛かってしまう）。 -->
    <Transition v-bind="AREA_LAYER_FADE">
      <div v-if="apiKey && isReady && isAreaLayer" ref="areaOverlay" class="pointer-events-none absolute inset-0 z-[2]">
        <!-- pointer-events は切る。ここで受けるとホイール・ドラッグ・ピンチが地図に届かず、
             ラベルの上ではページ全体がズーム/スクロールしてしまう（地図が動かない）。
             ポインタ操作は地図に素通りさせ、選択は地図の click を実測矩形で当て判定して行う
             （handleMapClick）。キーボード操作は pointer-events の影響を受けないため、
             Tab でフォーカスして Enter を押す経路はこの @click がそのまま担う。 -->
        <button
          v-for="dot in positionedAreaDots"
          :key="dot.tagWebPath"
          class="pointer-events-none absolute flex items-center justify-center"
          type="button"
          :aria-label="dot.name"
          :aria-pressed="dot.selected"
          :data-area-dot="dot.tagWebPath"
          :data-hit-priority="dot.hitPriority"
          :style="{
            left: `${dot.x}px`,
            top: `${dot.y}px`,
            width: `${dot.hitSize}px`,
            height: `${dot.hitSize}px`,
            transform: 'translate(-50%, -50%)',
            // 44px の当たり判定は近接ドットどうしで重なる（広域では数 px しか離れない組がある）。
            // 優先度の高い＝ラベルが出ている方を必ず前面にして、押せる方と読める方を一致させる。
            zIndex: dot.hitPriority,
          }"
          @click="handleAreaDotClick(dot.tagWebPath, dot.selected)"
        >
          <span aria-hidden="true" class="rounded-full transition-all" :class="dot.selected ? 'h-[9px] w-[9px] bg-gold3 ring-2 ring-white/80' : 'h-[7px] w-[7px] bg-gold4 ring-1 ring-white/70'"></span>
          <!-- ラベルは当たり判定の四角からはみ出して描かれる。当て判定を座標計算で再現すると
               ここがずれて「見えている文字が押せない」になるので、描画済みの矩形をそのまま使う
               （findAreaDotAt が data-area-label を実測する）。 -->
          <span
            v-show="dot.labelVisible"
            class="absolute whitespace-nowrap font-Spectral tracking-[0.5px] transition-colors"
            :class="dot.selected ? 'text-[22px] font-medium leading-[30px] text-gold3' : 'text-[16px] leading-[22px] text-gold4'"
            :style="dot.labelStyle"
            data-area-label
          >
            {{ dot.name }}
          </span>
        </button>
      </div>
    </Transition>

    <!-- ズームコントロール（Figma: マップ左上に白丸の −/+。Google 標準UIは無効化して自前で描画） -->
    <div v-if="apiKey && isReady" class="absolute left-3 top-3 z-10 flex items-center gap-0.5 rounded-sm bg-white p-1 shadow-[0px_4px_48px_0px_rgba(0,0,0,0.15)]">
      <button aria-label="Zoom out" class="flex h-8 w-8 items-center justify-center text-gray4 transition-colors hover:text-gray9" type="button" @click="zoomBy(-1)">
        <svg aria-hidden="true" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M5 12h14" stroke-linecap="round" /></svg>
      </button>
      <button aria-label="Zoom in" class="flex h-8 w-8 items-center justify-center text-gray4 transition-colors hover:text-gray9" type="button" @click="zoomBy(1)">
        <svg aria-hidden="true" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" stroke-linecap="round" /></svg>
      </button>
    </div>

    <!-- ビューポート内0件表示。ズームコントロール/トグルと被らないよう中央に配置し、
         クリックで体験のある範囲へ戻せるようにする（余白も確保）。
         エリアラベル層では出さない。体験ピン自体を描いていない層で「体験が無い」と報せても意味がなく、
         行き先ラベルの上に被るだけになるため。
         体験 0 件でもエリアが出せるならエリアへ誘導する（PR #711 レビュー）ので、ここが出るのは
         「体験もエリアも視野に無い」場合だけになる。 -->
    <div v-if="apiKey && isReady && !isAreaLayer && pins.length === 0" class="pointer-events-none absolute inset-0 z-10 flex items-center justify-center px-6">
      <button
        class="pointer-events-auto max-w-[320px] rounded-md bg-white/95 px-5 py-4 text-center font-Spectral text-sm leading-6 text-gray4 shadow-[0px_4px_48px_0px_rgba(0,0,0,0.15)] transition-colors hover:text-gold3"
        type="button"
        @click="resetView"
      >
        No experiences in this view.<br />
        Tap to view all experiences.
      </button>
    </div>

    <!-- 下部ドッキングのカード。体験プレビューとエリア紹介は層が排他なので同時には出ない。 -->
    <div v-if="selectedPin && !isAreaLayer" class="absolute inset-x-3 bottom-3 z-10 mx-auto max-w-[416px]">
      <ExperienceMapPreviewCard :on-close="handleClosePreview" :on-cta-click="handlePreviewCtaClick" :pin="selectedPin" />
    </div>
    <div v-else-if="selectedAreaDestination && isAreaLayer" class="absolute inset-x-3 bottom-3 z-10 mx-auto max-w-[416px]">
      <ExperienceMapAreaCard :destination="selectedAreaDestination" :on-card-click="handleAreaCardClick" :on-close="handleCloseAreaCard" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { MarkerClusterer, type Cluster, type Renderer } from '@googlemaps/markerclusterer';

import { useOptimizedImageUrl } from '@/wabunka-src/common/composables/general/useOptimizedImageUrl/useOptimizedImageUrl';
import ExperienceMapAreaCard from '@/wabunka-src/common/features/Experience/components/ExperienceMapView/ExperienceMapAreaCard.vue';
import ExperienceMapPreviewCard from '@/wabunka-src/common/features/Experience/components/ExperienceMapView/ExperienceMapPreviewCard.vue';
import type { MapPinBoundingBox } from '@/wabunka-src/common/features/Experience/composables/useWabunkaExperienceMapPins';
import {
  AREA_LAYER_MIN_ZOOM,
  type AreaDotHitCandidate,
  type AreaMarkerLayerViewModel,
  pickAreaDotAt,
  projectAreaDots,
  resolveAreaLabelPlacement,
} from '@/wabunka-src/common/features/Experience/core/areaMarkerLayer/areaMarkerLayer';
import {
  forcesPinLayer,
  initialMapLayerState,
  shouldPrefetchAreaData,
  type MapLayerEvent,
  nextMapLayerState,
  shouldRefitPins,
  startsAreaLayerSession,
  suppressesViewportNotification,
} from '@/wabunka-src/common/features/Experience/core/areaMarkerLayer/mapLayerState';
import type { WabunkaExperienceMapPinListViewModel, WabunkaExperienceMapPinViewModel } from '@/wabunka-src/common/features/Experience/models/wabunkaExperienceMapPin';
import type { CityLabelDir, SelectableDestination } from '@/wabunka-src/common/features/Featured/core/featuredDestinations/featuredDestinationsMap';
import { loadGoogleMapsLibraries } from '@/wabunka-src/common/utils/googleMapsLoader';

interface Props {
  /** 表示するピン（座標欠損は data 層で除外済み） */
  pins: WabunkaExperienceMapPinListViewModel;
  /**
   * ピン取得中か。取得前の 0 件を「視野に体験が無い」と誤読しないためのゲート。
   *
   * **任意にしない。** ピン配信は client 専用でマウント直後は必ず 0 件になるため、渡し忘れると
   * そのページは毎回エリアラベル層で開き、全ユーザーがエリアデータ 3 本を取得することになる。
   * 必須にしておけば渡し忘れが型で落ちる。
   */
  arePinsLoading: boolean;
  /** 選択中のピンID（プレビューカード表示対象） */
  selectedPinId: string | null;
  /** 広域ズーム時に描くエリアラベル層（JCAT-5434）。`layer === 'areas'` の間は体験ピン・クラスタを隠す。 */
  areaMarkerLayer: AreaMarkerLayerViewModel;
  /** 選択中の行き先（エリア紹介カードの表示対象）。未選択なら null。 */
  selectedAreaDestination: SelectableDestination | null;
  /** 地図の状態が変わったときのコールバック。判断は状態機械（core/mapLayerState）が出す。
   * - `forcePinLayer`: 体験ピン層に留めるべきか
   * - `shouldPrefetchAreaData`: エリアデータを取りに行くべきか */
  onZoomChange?: (zoom: number | null, decisions: { forcePinLayer: boolean; shouldPrefetchAreaData: boolean }) => void;
  /** エリアのラベル/ドットが選択・解除されたときのコールバック */
  onSelectArea?: (tagWebPath: string | null) => void;
  /** エリア紹介カードから行き先 LP へ遷移したときのコールバック（GA 計測用） */
  onAreaCardClick?: (destination: SelectableDestination) => void;
  /** エリアラベル層を**実際に描いているか**が変わったときのコールバック。
   * ズーム判定だけで層に入っても、画面内に描けるドットが無ければ描画はしていない。
   * GA の到達計測とカードのクリアはこちらに揃える（描いていない層を「到達」と数えないため）。 */
  onAreaLayerRenderedChange?: (rendered: boolean) => void;
  /** ハイライト中のピンID（リストカード hover 連動。プレビューは出さずマーカーのみ強調: F-15） */
  highlightedPinId?: string | null;
  /** 地図の描画範囲が変わったときのコールバック（ビューポート連動）。
   * null のときは広域表示のため絞り込まない（全件表示に戻す）。 */
  onBoundsChange?: (boundingBox: MapPinBoundingBox | null) => void;
  /** ピンが選択/選択解除されたときのコールバック */
  onSelectPin?: (pinId: string | null) => void;
  /** クラスタ（複数ピンのまとまり）がタップされたときのコールバック（GA 計測用。挙動はズームインのみ） */
  onClusterClick?: () => void;
  /** プレビューカードから体験詳細へ遷移したときのコールバック（GA 計測用） */
  onPreviewCtaClick?: (pin: WabunkaExperienceMapPinViewModel) => void;
  /** ユーザー操作（パン・ズーム等）でビューポートが変わったときのコールバック（GA 計測用）。
   * 初期表示の自動フィット直後の通知では呼ばれない（onBoundsChange のみ）。 */
  onViewportSearch?: () => void;
}

const props = defineProps<Props>();

// 日本全体のデフォルト中心・ズーム（初期表示。ピンがあれば fitBounds で上書き）。
//
// この既定ズームは **AREA_LAYER_MAX_ZOOM 以下**（＝エリアラベル層の内側）である点に注意。
// 初期表示は `isInitialView` ゲートで層に入らないよう守っているが、そのゲートが効かない経路
// （ユーザー操作後の resetView など）でここへ戻すと層に着地して体験が見えなくなる。
// 既定ズームへ戻す処理を足すときは、必ず「初期表示に戻す」扱いにすること（resetView 参照）。
const DEFAULT_CENTER = { lat: 36.2048, lng: 138.2529 };
const DEFAULT_ZOOM = 5;

/**
 * エリアラベル層に入ったときに合わせる日本全体の範囲。
 * 北海道〜九州を収め、沖縄・小笠原など離島は外す（「広域＝日本を俯瞰する」絵にする）。
 */
const JAPAN_BOUNDS = { south: 30, west: 128, north: 46, east: 146.5 };

// クラスタクリック時の 1 クリックあたりのズーム段数と上限（滑らかに寄せていく）
const CLUSTER_ZOOM_STEP = 3;
const MAX_CLUSTER_ZOOM = 16;
// 1 段ズームするごとの待ち時間（Google Maps の setZoom アニメーションを1段ずつ見せる）
const SMOOTH_ZOOM_INTERVAL_MS = 200;

// デザイントークン（Figma: Background/accent・Foreground(Border)/accent）
const PIN_SELECTED_COLOR = '#a48953';
const PIN_DEFAULT_COLOR = '#ffffff';
const CLUSTER_COLOR = '#826b3d';

// エリアラベル層（Figma 60038:7369 / 60038:7685）。
// タップ領域は 44x44px 以上（N-04）。ドット自体は 7〜9px なので透明な当たり判定を別に持つ。
const AREA_HIT_AREA_PX = 44;
/**
 * スクリムの不透明度。ベースマップの地名・道路・POI を沈めて行き先名を主役にするための値。
 * 上げすぎると地図の形が消えて「日本地図の上のラベル」に見えなくなるため、実機で見ながら詰める。
 */
const AREA_LAYER_SCRIM_OPACITY = 0.72;
/**
 * 層の出入りのフェード。切り替えの瞬間にスクリムとラベルがパッと入れ替わると、
 * 地図が別物に差し替わったように見えるため、短く溶かす。
 * `!opacity-0` はスクリムが inline style で opacity を持つため、それに勝たせる必要がある。
 */
const AREA_LAYER_FADE = {
  enterActiveClass: 'transition-opacity duration-300 ease-out',
  leaveActiveClass: 'transition-opacity duration-200 ease-in',
  enterFromClass: '!opacity-0',
  leaveToClass: '!opacity-0',
} as const;
/** 沈めたベースマップの文字がラベルに透けても読めるように、薄い白の縁取りを敷く。 */
const AREA_LABEL_TEXT_SHADOW = '0 0 4px rgba(244,241,237,0.95), 0 0 10px rgba(244,241,237,0.85)';
/** ドット中心からラベルまでの距離。配置解決（resolveAreaLabelPlacement）に渡す値と一致させる。 */
const AREA_LABEL_GAP_PX = 10;
/** ラベルを出すドットどうしに最低限空ける距離。近すぎる2つに名前を付けるとどちらの地点か分からなくなる。 */
const AREA_LABEL_MIN_DOT_SEPARATION_PX = 28;
/**
 * ラベルの向きごとの配置。core の `labelBoxOf` が判定に使う位置と一致させる必要があるため、
 * 距離は `AREA_LABEL_GAP_PX` から算出する（クラス名に数値を直書きすると二重管理になり、
 * 片方だけ変えたときに判定位置と実描画位置がずれる）。
 */
const areaLabelDirStyle = (dir: CityLabelDir): Record<string, string> => {
  const gap = `calc(50% + ${AREA_LABEL_GAP_PX}px)`;
  switch (dir) {
    case 'right':
      return { left: gap, top: '50%', transform: 'translateY(-50%)' };
    case 'left':
      return { right: gap, top: '50%', transform: 'translateY(-50%)' };
    case 'up':
      return { bottom: gap, left: '50%', transform: 'translateX(-50%)' };
    case 'down':
      return { top: gap, left: '50%', transform: 'translateX(-50%)' };
  }
};
/** ラベルの行の高さ（実測の代わりに font-size から出す係数）。 */
const AREA_LABEL_LINE_HEIGHT = 1.4;
/** 場所取りは常に選択中サイズで行う（選択で文字が大きくなっても収まりが変わらないように）。 */
const AREA_LABEL_ACTIVE_FONT_SIZE_PX = 22;

const runtimeConfig = useRuntimeConfig();
const apiKey = computed<string>(() => (runtimeConfig.public.GOOGLE_MAP_API_KEY as string) ?? '');
const mapId = computed<string>(() => (runtimeConfig.public.GOOGLE_MAP_ID as string) ?? '');

// マーカーのサムネイルは 40〜48px 表示。生の CMS 画像をそのまま読むと帯域が無駄なため、
// Cloudflare 画像変換で小さく最適化した URL を使う（Retina 対応で表示サイズの約2倍を指定）。
const getOptimizedImageUrl = useOptimizedImageUrl();

const mapContainer = ref<HTMLElement | null>(null);
const areaOverlay = ref<HTMLElement | null>(null);
const isReady = ref(false);

const selectedPin = computed<WabunkaExperienceMapPinViewModel | null>(() => {
  if (!props.selectedPinId) return null;
  return props.pins.find((pin) => pin.id === props.selectedPinId) ?? null;
});

/**
 * ズーム判定として層に入っているか（投影には依存しない）。
 * 投影の更新やデータ先読みなど、「描けるかどうかの前」の判断はこちらを使う。
 */
const isAreaLayerRequested = computed<boolean>(() => props.areaMarkerLayer.layer === 'areas');

/** 選択中 or ホバー中ならマーカーを accent 表示にする（F-14/F-15） */
const isActivePin = (pinId: string): boolean => pinId === props.selectedPinId || pinId === props.highlightedPinId;

let map: google.maps.Map | null = null;
let markerLibrary: google.maps.MarkerLibrary | null = null;
let clusterer: MarkerClusterer | null = null;
let markerByPinId = new Map<string, google.maps.marker.AdvancedMarkerElement>();
let zoomListener: google.maps.MapsEventListener | null = null;
let boundsListener: google.maps.MapsEventListener | null = null;
let dragListener: google.maps.MapsEventListener | null = null;
// エリアオーバーレイの投影を描画フレームに合わせて更新するための予約 ID
let areaViewportFrame: number | null = null;
let idleListener: google.maps.MapsEventListener | null = null;
let clickListener: google.maps.MapsEventListener | null = null;
/**
 * 層の状態機械（core/areaMarkerLayer/mapLayerState）。
 *
 * 以前はここに可変フラグを 7 つ並べていたが、フラグどうしの噛み合わせが壊れても動かすまで
 * 分からず、同じ種類の不具合を何度も作った。遷移は純粋関数へ切り出してテストで固定し、
 * ここは Google Maps のイベントを翻訳して、返ってきた判断に従うだけにする。
 */
let layerState = initialMapLayerState;

/**
 * イベントを状態機械へ流す。層のセッションが始まったか（＝カメラを日本全体へ合わせるべきか）を返す。
 * 副作用をここで起こさないのは、状態更新と描画操作の順序を呼び出し側で明示させるため。
 */
const applyLayerEvent = (event: MapLayerEvent): boolean => {
  const previous = layerState;
  layerState = nextMapLayerState(previous, event);
  return startsAreaLayerSession(previous, layerState);
};

/** これから自分でズームを変える、と申告する（ユーザー操作と数えないため）。 */
const beginAutoZoom = () => {
  applyLayerEvent({ type: 'autoZoomStarted' });
};

// 自動 fitBounds（fitToPins）由来の idle 通知を GA 計測から除外するための一発フラグ。
// fitToPins 実行ごとに立て、直後の通知1回で消費する（消費後＝ユーザー操作による変更とみなす）
let suppressNextViewportSearch = false;
let boundsDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let resizeObserver: ResizeObserver | null = null;
// アンマウント済みフラグ。initMap は loadGoogleMapsLibraries の await を跨ぐため、
// 読み込み中にアンマウントされた場合は以降のマップ生成・リスナー登録を行わない（例外・リーク防止）。
let isUnmounted = false;

/**
 * マップピンの DOM 要素を生成する（scoped CSS が届かないため inline style を使用）。
 * Figma: サムネイル画像を白（選択時=accent）の1px枠で囲み、下向き三角形のポインタを付ける。
 * 選択時は枠色を accent にし、画像を 48px に拡大する。
 */
const buildPinContent = (pin: WabunkaExperienceMapPinViewModel, selected: boolean): HTMLElement => {
  const size = selected ? 48 : 40;
  const frameColor = selected ? PIN_SELECTED_COLOR : PIN_DEFAULT_COLOR;

  const wrapper = document.createElement('div');
  wrapper.style.cssText = 'display:flex;flex-direction:column;align-items:center;cursor:pointer;filter:drop-shadow(0 0 4px rgba(0,0,0,0.2));';

  const frame = document.createElement('div');
  frame.style.cssText = `background:${frameColor};padding:1px;border-radius:1px;line-height:0;`;

  if (pin.thumbnailImageUrl) {
    const img = document.createElement('img');
    // 表示は最大 48px の正方形（object-fit:cover）。Retina 対応で 2 倍の 96px 正方形に crop 変換する。
    img.src = getOptimizedImageUrl(pin.thumbnailImageUrl, { width: 96, height: 96, fit: 'cover' });
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.style.cssText = `width:${size}px;height:${size}px;object-fit:cover;border-radius:1px;display:block;`;
    frame.appendChild(img);
  } else {
    const placeholder = document.createElement('div');
    placeholder.style.cssText = `width:${size}px;height:${size}px;border-radius:1px;background:#dad6d0;`;
    frame.appendChild(placeholder);
  }

  const pointer = document.createElement('div');
  pointer.style.cssText = `width:0;height:0;border-left:12px solid transparent;border-right:12px solid transparent;border-top:12px solid ${frameColor};margin-top:-1px;`;

  wrapper.appendChild(frame);
  wrapper.appendChild(pointer);
  return wrapper;
};

/**
 * クラスタマーカーのレンダラ。
 * Figma: accent(#826b3d) の円形バッジ（細い外側リング＋白の Spectral Medium 件数）。件数の桁で幅が伸びる。
 */
const buildClusterRenderer = (advancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement): Renderer => ({
  render: (cluster: Cluster) => {
    const inner = document.createElement('div');
    inner.textContent = String(cluster.count);
    inner.style.cssText = [
      `background:${CLUSTER_COLOR}`,
      'border-radius:9999px',
      // 幅=高さ=56 の正円（box-sizing:border-box で padding 込み）。桁が増えたら横に伸びてピルになる
      'box-sizing:border-box',
      'min-width:56px',
      'height:56px',
      'padding:0 8px',
      'display:flex',
      'align-items:center',
      'justify-content:center',
      'color:#ffffff',
      "font-family:'Spectral',serif",
      'font-weight:500',
      'font-size:20px',
      'line-height:28px',
      'letter-spacing:0.5px',
    ].join(';');

    const ring = document.createElement('div');
    ring.style.cssText = `border:1px solid ${CLUSTER_COLOR};border-radius:9999px;padding:2px;box-shadow:0 0 8px rgba(0,0,0,0.2);display:inline-flex;cursor:pointer;`;
    ring.appendChild(inner);

    return new advancedMarkerElement({ position: cluster.position, content: ring });
  },
});

/** 現在の地図の描画範囲を bbox として取得する。
 * ズームアウトで経度が日付変更線を跨ぐ（ne.lng < sw.lng）／ほぼ全球を覆う場合は
 * 絞り込まない（null = 全件表示）。狭いマップや広域表示で誤って0件に絞り込むのを防ぐ。 */
const currentBoundingBox = (): MapPinBoundingBox | null => {
  if (!map) return null;
  const bounds = map.getBounds();
  if (!bounds) return null;
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  const lngSpan = ne.lng() - sw.lng();
  if (lngSpan <= 0 || lngSpan >= 170) return null;
  return {
    southWestLatitude: sw.lat(),
    southWestLongitude: sw.lng(),
    northEastLatitude: ne.lat(),
    northEastLongitude: ne.lng(),
  };
};

/** 全ピンが収まるように地図をフィットする。
 * 自動 fitBounds（初回フィット・リサイズ補正）由来の idle はユーザー操作ではないため、
 * 直後の1回分の onViewportSearch（GA 計測）を抑止するフラグを立てる。 */
const fitToPins = () => {
  if (!map || props.pins.length === 0) return;
  suppressNextViewportSearch = true;
  beginAutoZoom();
  // 0x0 のコンテナで走ったフィットは当てにならない。実寸が出たらやり直す（ResizeObserver）。
  applyLayerEvent({
    type: 'pinsFitted',
    hasMeasuredPane: (mapContainer.value?.clientWidth ?? 0) > 0 && (mapContainer.value?.clientHeight ?? 0) > 0,
  });
  const bounds = new google.maps.LatLngBounds();
  props.pins.forEach((pin) => bounds.extend({ lat: pin.latitude, lng: pin.longitude }));
  map.fitBounds(bounds, 48);
};

// ===== エリアラベル層のオーバーレイ（地図の外側に自前で描く）=====
// 地図の描画範囲とペインの実寸から、行き先の緯度経度をオーバーレイ上のピクセルへ投影する。
// idle（パン・ズーム完了）とペインのリサイズで作り直す。

/** 投影の基準（地図の描画範囲）。idle ごとに更新する。 */
const areaViewport = ref<{ swLat: number; swLng: number; neLat: number; neLng: number } | null>(null);
/** オーバーレイの実寸。ResizeObserver で更新する。 */
const areaPane = ref<{ width: number; height: number }>({ width: 0, height: 0 });
/** ラベルの実測サイズ（canvas 計測）。フォント確定後に測り直す。 */
const areaLabelSizes = ref(new Map<string, { width: number; height: number }>());

const syncAreaViewport = () => {
  const bounds = map?.getBounds();
  if (!bounds) {
    areaViewport.value = null;
    return;
  }
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  areaViewport.value = { swLat: sw.lat(), swLng: sw.lng(), neLat: ne.lat(), neLng: ne.lng() };
};

/**
 * エリアオーバーレイの投影基準を次の描画フレームで更新する。
 *
 * `idle`（移動完了）だけで更新すると、ドラッグ中とズームアニメーション中はドット・ラベルが
 * 地図に追従せず取り残される。層に切り替わった直後もドットが出るまで idle を待つことになり、
 * 「エリアが出るのが遅い」体感になる。`bounds_changed` は連続で発火するため、
 * requestAnimationFrame でフレームに 1 回へ間引く。
 */
const scheduleAreaViewportSync = () => {
  // 体験ピン層では描くものが無いので、無駄なリアクティブ更新を走らせない
  if (!isAreaLayerRequested.value || areaViewportFrame != null) return;
  areaViewportFrame = requestAnimationFrame(() => {
    areaViewportFrame = null;
    syncAreaViewport();
  });
};

/**
 * 未表示のラベルは DOM に無いので canvas で文字幅を測る（フォント確定後に呼ぶこと）。
 * 場所取りは選択中サイズで行う（選択されて文字が大きくなっても収まりが変わらないように）。
 */
const measureAreaLabelSizes = () => {
  const context = document.createElement('canvas').getContext('2d');
  if (!context) return;
  context.font = `500 ${AREA_LABEL_ACTIVE_FONT_SIZE_PX}px Spectral, Georgia, serif`;
  const sizes = new Map<string, { width: number; height: number }>();
  for (const dot of props.areaMarkerLayer.dots) {
    sizes.set(dot.tagWebPath, { width: context.measureText(dot.name).width, height: AREA_LABEL_ACTIVE_FONT_SIZE_PX * AREA_LABEL_LINE_HEIGHT });
  }
  areaLabelSizes.value = sizes;
};

/** 行き先の緯度経度 → オーバーレイ上のピクセル。 */
const areaDotPositions = computed(() =>
  projectAreaDots({
    dots: props.areaMarkerLayer.dots,
    viewport: areaViewport.value,
    pane: areaPane.value,
    cullMargin: AREA_HIT_AREA_PX,
  }),
);

/** ラベルの向きと表示可否（実測幅を core に渡して決める）。 */
const areaLabelPlacements = computed(() =>
  resolveAreaLabelPlacement({
    dots: props.areaMarkerLayer.dots,
    positions: areaDotPositions.value,
    labelSizes: areaLabelSizes.value,
    pane: areaPane.value,
    gap: AREA_LABEL_GAP_PX,
    hitAreaSize: AREA_HIT_AREA_PX,
    minDotSeparation: AREA_LABEL_MIN_DOT_SEPARATION_PX,
  }),
);

/** テンプレートが描くドット一覧（位置・選択状態・ラベルの向きと可否を畳み込んだもの）。 */
const positionedAreaDots = computed(() =>
  props.areaMarkerLayer.dots.flatMap((dot) => {
    const position = areaDotPositions.value.get(dot.tagWebPath);
    if (!position) return [];
    const placement = areaLabelPlacements.value.get(dot.tagWebPath);
    return [
      {
        tagWebPath: dot.tagWebPath,
        name: dot.name,
        x: position.x,
        y: position.y,
        selected: dot.tagWebPath === props.selectedAreaDestination?.tagWebPath,
        labelVisible: placement?.visible ?? false,
        labelStyle: { textShadow: AREA_LABEL_TEXT_SHADOW, ...areaLabelDirStyle(placement?.dir ?? dot.preferredLabelDir) },
        hitSize: placement?.hitSize ?? AREA_HIT_AREA_PX,
        // labelPriority は 0 が最優先。z-index は大きい方が前面なので反転させる。
        hitPriority: props.areaMarkerLayer.dots.length - dot.labelPriority,
      },
    ];
  }),
);

/**
 * エリアラベル層を「実際に描けているか」。true の間は体験ピン・クラスタを出さない。
 *
 * ズームだけでなく**画面内に描けるドットがあるか**まで見る。行き先の都市点は本州＋香川しか無いため、
 * 層に入ったまま九州や北海道へパンすると可視ドットが 0 件になる。ズームだけで判定すると、
 * そこでピンもクラスタもラベルも 0 件表示も出ない「ベージュ一色・復帰導線なし」に落ちる。
 * core 側は総数で同じ穴を塞いでいるが、画面外フィルタはこちら側にあるため両方で見る必要がある。
 *
 * NOTE: これは投影結果に依存するので、投影を更新する側（scheduleAreaViewportSync）から参照しないこと。
 * 参照すると「ドットが無いから同期しない → 永久にドットが出ない」で固まる。
 */
const isAreaLayer = computed<boolean>(() => isAreaLayerRequested.value && positionedAreaDots.value.length > 0);

/** ドットのタップは選択のトグル。地図は動かさない（カードの紹介文を読ませるため）。 */
const handleAreaDotClick = (tagWebPath: string, selected: boolean) => {
  props.onSelectArea?.(selected ? null : tagWebPath);
};

/**
 * 画面座標にあるエリアドットを探す。
 *
 * オーバーレイは pointer-events を切ってあるため（地図のホイール・ドラッグ・ピンチを殺さないため）、
 * ブラウザの当たり判定は使えない。描画済みの矩形を実測して自前で当てる。
 *
 * ラベルはボタンの矩形からはみ出して描かれるので、ボタンとラベルの両方を見る。座標から計算し直すと
 * 描画とずれて「見えている文字が押せない」になる。
 *
 * ここは描画済みの矩形を集めるだけで、どれを選ぶかの規則は core（`pickAreaDotAt`）が持つ。
 */
const findAreaDotAt = (clientX: number, clientY: number): string | null => {
  const buttons = areaOverlay.value?.querySelectorAll<HTMLElement>('[data-area-dot]');
  if (!buttons) return null;

  const candidates: AreaDotHitCandidate[] = [];
  buttons.forEach((button) => {
    const tagWebPath = button.dataset.areaDot;
    if (tagWebPath == null) return;
    // 出ていないラベル（v-show の display:none）は offsetParent が null になる
    const label = button.querySelector<HTMLElement>('[data-area-label]');
    candidates.push({
      tagWebPath,
      priority: Number(button.dataset.hitPriority ?? 0),
      dotRect: button.getBoundingClientRect(),
      labelRect: label != null && label.offsetParent != null ? label.getBoundingClientRect() : null,
    });
  });

  return pickAreaDotAt(candidates, clientX, clientY);
};

/**
 * 地図のクリック。エリアラベル層では、オーバーレイを素通りしてきたクリックをここで拾う。
 *
 * 余白のクリックでは何もしない（選択解除はカードの × で行う。地図を触るたびに紹介文が閉じると読めない）。
 */
const handleMapClick = (event: google.maps.MapMouseEvent) => {
  if (!isAreaLayer.value) return;
  const domEvent = event.domEvent as MouseEvent | undefined;
  if (domEvent == null) return;
  const tagWebPath = findAreaDotAt(domEvent.clientX, domEvent.clientY);
  if (tagWebPath == null) return;
  handleAreaDotClick(tagWebPath, tagWebPath === props.selectedAreaDestination?.tagWebPath);
};

/**
 * エリアラベル層に入った瞬間に、日本全体が収まる位置へ一度だけ合わせる。
 *
 * 合わせないと「ユーザーがズームアウトを止めた位置」がそのまま残り、中国大陸やサハリンが
 * 画面の大半を占める絵になる。この層は日本を俯瞰して土地を知るためのものなので、
 * 日本が主役になる画角へ寄せる。
 *
 * 層は現在のズームから導出せずユーザー操作で保持するラッチなので、この自動フィットの着地点で
 * 層が反転することはない（以前はクランプで押さえていたが、ラッチ化で不要になった）。
 * 層を出るまで一度きりなので、そこからさらにズームアウトする操作も妨げない。
 */
const fitToJapan = () => {
  if (!map) return;
  suppressNextViewportSearch = true;
  beginAutoZoom();
  map.fitBounds(new google.maps.LatLngBounds({ lat: JAPAN_BOUNDS.south, lng: JAPAN_BOUNDS.west }, { lat: JAPAN_BOUNDS.north, lng: JAPAN_BOUNDS.east }), 0);
};

/**
 * イベントを流し、広域層のセッションが始まったならカメラを日本全体へ合わせる。
 *
 * 描画状態の watch だけに任せない。空由来で描いている最中にズームアウトすると、描画は
 * true のまま変わらないので watch が発火せず、カメラも検索通知の抑止も効かないまま広域層に入る。
 */
const applyLayerEventAndFit = (event: MapLayerEvent) => {
  if (applyLayerEvent(event)) fitToJapan();
};

/** ピンからマーカーを組み立て、クラスタラに登録する。初回のみピン全体に fitBounds する。 */
const rebuildMarkers = () => {
  if (!map || !markerLibrary || !clusterer) return;
  const { AdvancedMarkerElement } = markerLibrary;

  clusterer.clearMarkers();
  markerByPinId = new Map();

  // エリアラベル層では体験ピン・クラスタを出さない（層の切り替え）
  if (isAreaLayer.value) return;

  const markers = props.pins.map((pin) => {
    const active = isActivePin(pin.id);
    const marker = new AdvancedMarkerElement({
      position: { lat: pin.latitude, lng: pin.longitude },
      content: buildPinContent(pin, active),
      title: pin.name,
      zIndex: active ? 1 : undefined,
    });
    marker.addListener('gmp-click', () => props.onSelectPin?.(pin.id));
    markerByPinId.set(pin.id, marker);
    return marker;
  });

  clusterer.addMarkers(markers);

  // 初回のみ全ピンにフィット（以降はビューポート連動の再取得で refit しない＝無限ループ防止）
  if (!layerState.hasFittedPins && props.pins.length > 0) fitToPins();
};

/** 対象マーカーの見た目（枠色・サイズ）を現在のアクティブ状態から再計算して更新する */
const refreshMarkerAppearance = (pinId: string | null) => {
  if (!pinId) return;
  const marker = markerByPinId.get(pinId);
  const pin = props.pins.find((p) => p.id === pinId);
  if (!marker || !pin) return;
  const active = isActivePin(pinId);
  marker.content = buildPinContent(pin, active);
  marker.zIndex = active ? 1 : null;
};

// ホバー中ピンが属するクラスタのバッジ要素（強調解除用に保持）
let emphasizedClusterEl: HTMLElement | null = null;

/** 指定ピンが「複数マーカーのクラスタ」に含まれる場合、その描画バッジ要素を返す（個別表示/未クラスタは null）。 */
const findClusterBadgeElement = (pinId: string): HTMLElement | null => {
  if (!clusterer) return null;
  const marker = markerByPinId.get(pinId);
  if (!marker) return null;
  // `clusters` はライブラリ内部で protected のため cast して参照する（実体は存在）
  const clusters = (clusterer as unknown as { clusters?: Cluster[] }).clusters;
  const cluster = clusters?.find((c) => (c.count ?? 0) > 1 && c.markers?.includes(marker));
  const content = (cluster?.marker as google.maps.marker.AdvancedMarkerElement | undefined)?.content;
  return content instanceof HTMLElement ? content : null;
};

/**
 * リストカード hover 連動の強調（F-15）。
 * - 個別マーカー（未クラスタ）は buildPinContent 側で accent 表示に切り替わる。
 * - クラスタに含まれる場合はそのバッジを拡大して「その体験がここにある」ことを示す。
 */
const setHoverEmphasis = (pinId: string | null, on: boolean) => {
  if (!pinId) return;
  refreshMarkerAppearance(pinId);
  const badge = findClusterBadgeElement(pinId);
  if (on) {
    if (emphasizedClusterEl && emphasizedClusterEl !== badge) {
      emphasizedClusterEl.style.transform = '';
      emphasizedClusterEl.style.zIndex = '';
    }
    if (badge) {
      badge.style.transition = 'transform 0.15s ease';
      badge.style.transform = 'scale(1.15)';
      badge.style.zIndex = '2';
      emphasizedClusterEl = badge;
    }
  } else if (badge) {
    badge.style.transform = '';
    badge.style.zIndex = '';
    if (emphasizedClusterEl === badge) emphasizedClusterEl = null;
  }
};

/** 現在のズームを親へ通知する（層の判定・エリアデータの先読み）。 */
const emitZoomChange = () => {
  const zoom = map?.getZoom() ?? null;
  props.onZoomChange?.(zoom, { forcePinLayer: forcesPinLayer(layerState), shouldPrefetchAreaData: shouldPrefetchAreaData(layerState) });
};

/**
 * 地図のズームが変わったとき。自分で仕掛けたぶんは無視し、ユーザー操作のときだけ層を切り替える。
 *
 * 層に入る条件は「閾値以下」かつ「基準の画角より引いている」。後者が無いと、一度寄ってから
 * 同じ画角に戻したときや 0 件リセット後にズームインしたときに広域表示になり、操作と逆向きになる。
 */
const handleZoomChanged = () => {
  applyLayerEventAndFit({ type: 'zoomChanged', zoom: map?.getZoom() ?? null });
  emitZoomChange();
};

const emitBoundsChange = () => {
  // エリアラベル層では通知しない。この層の地図は「日本全体を俯瞰する」ためのもので検索操作ではない。
  // 通知すると fitToJapan の bbox がそのまま親へ流れ、左リストが黙って「日本全国・1ページ目」に
  // 差し替わってしまう（層を出しても戻らないので検索コンテキストが失われる）。層の中のパンごとに
  // ピン再取得が走り、課金監視用の map_viewport_search も嵩上げされる。
  // 層を出たときに改めて 1 回だけ通知する（下の watch(isAreaLayer)）。
  // 判定は「層に入っているモードか」で行う（描けているかではない）。可視ドットが 0 になる
  // 九州・北海道へのパンで通知が復活すると、左リストが黙ってその範囲に差し替わってしまう。
  if (suppressesViewportNotification(layerState)) {
    // fitToJapan が立てた抑止フラグをここで捨てる。持ち越すと層を出た最初の通知を食い、
    // 課金監視用の map_viewport_search が 1 往復ごとに 1 回過小計上される。
    suppressNextViewportSearch = false;
    return;
  }
  // 初回フィット前（既定表示）はビューポート通知しない。
  // 特にモバイルの狭いマップだと既定表示の範囲がピンを外し、誤って0件に絞り込んでしまうため。
  if (!layerState.hasFittedPins) return;
  // 広域表示（bbox=null）も含めて通知する（null のとき呼び出し側は全件表示に戻す）
  props.onBoundsChange?.(currentBoundingBox());
  // 自動 fitBounds（初回フィット・ResizeObserver のワールド表示補正）直後の idle は
  // ユーザー操作ではないため、ビューポート変更の GA 計測から除外する
  if (suppressNextViewportSearch) {
    suppressNextViewportSearch = false;
  } else {
    props.onViewportSearch?.();
  }
};

// クラスタクリック時の段階ズーム用タイマー
let smoothZoomTimer: ReturnType<typeof setTimeout> | null = null;
/** 目標ズームまで 1 段ずつ setZoom してアニメーションさせる（一気に fitBounds しない）。 */
const smoothZoomTo = (targetZoom: number) => {
  if (!map) return;
  if (smoothZoomTimer) clearTimeout(smoothZoomTimer);
  const step = () => {
    if (!map) return;
    const current = map.getZoom() ?? DEFAULT_ZOOM;
    if (current >= targetZoom) return;
    map.setZoom(current + 1);
    smoothZoomTimer = setTimeout(step, SMOOTH_ZOOM_INTERVAL_MS);
  };
  step();
};

const initMap = async () => {
  if (!apiKey.value || !mapContainer.value) return;

  const { maps, marker } = await loadGoogleMapsLibraries(apiKey.value);
  // 読み込み中にアンマウントされた／コンテナが失われた場合は中断する
  if (isUnmounted || !mapContainer.value) return;
  markerLibrary = marker;

  map = new maps.Map(mapContainer.value, {
    center: DEFAULT_CENTER,
    zoom: DEFAULT_ZOOM,
    // Map ID は AdvancedMarkerElement / ブランド地図スタイルに必要。未設定時は undefined（マーカー非表示）。
    mapId: mapId.value || undefined,
    // 日本を俯瞰する以上に引く意味が無く、引きすぎると可視範囲が日付変更線を跨いで投影が壊れる
    minZoom: AREA_LAYER_MIN_ZOOM,
    disableDefaultUI: true,
    // ズームは Figma 準拠の自前 −/+ コントロールを使うため Google 標準は無効化
    zoomControl: false,
    gestureHandling: 'greedy',
    clickableIcons: false,
  });

  clusterer = new MarkerClusterer({
    map,
    renderer: buildClusterRenderer(marker.AdvancedMarkerElement),
    // 既定の fitBounds は一気にズームしてしまうため、クラスタ中心へ寄せつつ 1 段ずつ滑らかにズームインする
    onClusterClick: (_event, cluster, clickedMap) => {
      props.onClusterClick?.();
      clickedMap.panTo(cluster.position);
      const current = clickedMap.getZoom() ?? DEFAULT_ZOOM;
      smoothZoomTo(Math.min(current + CLUSTER_ZOOM_STEP, MAX_CLUSTER_ZOOM));
    },
  });
  rebuildMarkers();

  // パン/ズーム完了ごとにビューポートを通知（連続操作をデバウンス）
  idleListener = map.addListener('idle', () => {
    applyLayerEvent({ type: 'moveSettled', zoom: map?.getZoom() ?? null });
    // エリアオーバーレイの投影基準はデバウンスせず即座に合わせる（ドットが地図から遅れて見えるため）
    syncAreaViewport();
    if (boundsDebounceTimer) clearTimeout(boundsDebounceTimer);
    boundsDebounceTimer = setTimeout(emitBoundsChange, 300);
  });

  // ズームを親へ通知（層の判定とエリアデータの先読みの起点）。
  // idle ではなく zoom_changed で拾うことで、パン中の無駄な通知を避けつつ切り替えを遅らせない。
  zoomListener = map.addListener('zoom_changed', handleZoomChanged);
  clickListener = map.addListener('click', handleMapClick);
  // ドラッグ・ズーム中もエリアのドット/ラベルを地図に追従させる（idle だけだと置いていかれる）
  boundsListener = map.addListener('bounds_changed', scheduleAreaViewportSync);
  // ドラッグは必ずユーザー操作。ここで先読みを起こしておくと、そのままズームアウトしたときに間に合う
  dragListener = map.addListener('dragstart', () => {
    applyLayerEventAndFit({ type: 'mapDragged', zoom: map?.getZoom() ?? null });
    emitZoomChange();
  });
  // 初期ズーム（fitBounds 前の既定値）も通知しておく。以降 fitBounds で変われば zoom_changed が拾う。
  emitZoomChange();
  syncAreaViewport();

  // ハイドレーション直後はコンテナサイズが未確定で fitBounds がワールド表示になることがある。
  // コンテナのサイズ確定時、ワールド表示（zoom<4）に陥っていたらピンにフィットし直す。
  // エリアオーバーレイの投影はペインの実寸に依存するため、ここで一緒に拾う。
  resizeObserver = new ResizeObserver(([entry]) => {
    const { width, height } = entry.contentRect;
    areaPane.value = { width, height };
    // ハイドレーション直後や SP のトグル前は、地図が 0x0 のコンテナに載ったまま初回フィットが走る。
    // その結果は当てにならないので、実寸が出た時点でフィットし直す。
    // ズーム値では判定しない（minZoom を入れたため「ワールド表示に落ちる」現象が起きなくなり、
    // 閾値ベースの復帰条件が構造的に成立しなくなった）。
    if (map && props.pins.length > 0 && shouldRefitPins(layerState, width > 0 && height > 0)) fitToPins();
  });
  resizeObserver.observe(mapContainer.value);

  // Spectral の読み込み前後で文字幅が変わるため、確定してから測り直す。
  measureAreaLabelSizes();
  document.fonts?.ready.then(measureAreaLabelSizes).catch(() => undefined);

  isReady.value = true;
};

const handleClosePreview = () => {
  props.onSelectPin?.(null);
};

const handlePreviewCtaClick = () => {
  if (selectedPin.value) props.onPreviewCtaClick?.(selectedPin.value);
};

/** 自前ズームコントロール（Figma: 左上 −/+）。1段ずつ増減する。 */
const zoomBy = (delta: number) => {
  if (!map) return;
  const current = map.getZoom() ?? DEFAULT_ZOOM;
  map.setZoom(current + delta);
};

/**
 * ビューポート内0件時に、体験が見える状態（＝初期表示）へ戻す。
 *
 * このボタンは `pins.length === 0` のときしか出ないので、「全ピンにフィットする」分岐は
 * 到達しない。日本全体へ戻して bbox を広げ、親にピンを取り直させるのが唯一の道筋。
 *
 * ただし既定ズームは閾値の内側なので、そのまま戻すとエリアラベル層に着地して
 * 「Tap to view all experiences」を押したのに体験が出ない。**初期表示に戻す**扱いにして
 * `isInitialView` ゲートを効かせ、体験ピン層で復帰させる。
 */
const resetView = () => {
  if (!map) return;
  beginAutoZoom();
  map.setCenter(DEFAULT_CENTER);
  map.setZoom(DEFAULT_ZOOM);
  // ユーザーのズーム操作履歴と基準の画角を引き直して「初期表示」に戻す（層に入らせない）
  applyLayerEvent({ type: 'viewReset' });
  emitZoomChange();
};

const handleCloseAreaCard = () => {
  props.onSelectArea?.(null);
};

const handleAreaCardClick = () => {
  if (props.selectedAreaDestination) props.onAreaCardClick?.(props.selectedAreaDestination);
};

// ピンが変わったらマーカーを作り直す（ビューポート連動の再取得を含む）
watch(
  () => props.pins,
  () => {
    if (isReady.value) rebuildMarkers();
  },
);

/**
 * 視野内に体験ピンがあるか。取得中は「ある」として扱う（取得前の 0 件で層を切り替えないため）。
 */
const hasExperiencePinsInView = computed<boolean>(() => props.pins.length > 0 || props.arePinsLoading);

// 視野が空になったらエリアへ誘導し、体験が戻ってきたら体験ピン層へ戻す（PR #711 レビュー）。
//
// 有無が変わったときだけでなく**ピンが届くたび**に流す。広域層のあいだは再取得を止めているため、
// 層を抜けた直後のピンは古い。状態機械はそれを「古い」と扱って層の判断から外しており、
// 古さが解けるのは新しいピンが届いたときだけ。有無が同じだと流さない作りにすると、
// ズームアウト前と同じく体験がある場所へ戻ったときに古さが解けず、層に居座ってしまう。
//
// マウント時点で既に 0 件のことがある（SP でリストから Map タブへ切り替えた直後など）ので immediate。
watch(
  [() => props.pins, () => props.arePinsLoading],
  () => {
    applyLayerEventAndFit({ type: 'experiencePinsChanged', hasPins: hasExperiencePinsInView.value });
    emitZoomChange();
  },
  { immediate: true },
);

// 選択ピンが変わったら該当マーカー（旧・新）の見た目を更新する
watch(
  () => props.selectedPinId,
  (newId, oldId) => {
    refreshMarkerAppearance(oldId ?? null);
    refreshMarkerAppearance(newId ?? null);
  },
);

// ホバーハイライト（リストカード連動: F-15）が変わったら該当マーカーの見た目を更新する
watch(
  () => props.highlightedPinId,
  (newId, oldId) => {
    setHoverEmphasis(oldId ?? null, false);
    setHoverEmphasis(newId ?? null, true);
  },
);

// 層が切り替わったら体験ピン側を作り直す（エリア側はテンプレートがリアクティブに描き直す）。
// 層に入った直後は投影基準が古い（前回 idle 時点の bounds）ことがあるので合わせ直す。
watch(isAreaLayerRequested, (requested) => {
  if (!isReady.value) return;
  if (requested) {
    // カメラはここでは動かさない。データが来ない／失敗したときに、ラベルが出ないまま
    // 日本全体へ飛んだ画だけが残るため。実際に描けた時点（下の watch）で動かす。
  } else {
    // 層を出たら、その時点のビューポートで検索を再開する（層の中では止めていたため）。
    // ここは zoom_changed 起点＝ズーム途中なので bbox が確定していない。即時に通知すると
    // 途中の bbox で 1 回、300ms 後の idle で確定 bbox にもう 1 回とピン取得が 2 本出る。
    // 既存のデバウンスに載せて idle の 1 本に寄せる。
    if (boundsDebounceTimer) clearTimeout(boundsDebounceTimer);
    boundsDebounceTimer = setTimeout(emitBoundsChange, 300);
  }
  syncAreaViewport();
});

// 実際に描けているかが変わったら、体験ピン側を出し入れし、親にも伝える
watch(isAreaLayer, (areaLayer) => {
  props.onAreaLayerRenderedChange?.(areaLayer);
  if (!isReady.value) return;
  rebuildMarkers();
  // 実際に描けた最初の一度だけ、日本が主役になる画角へ寄せる
  if (applyLayerEvent({ type: 'areaRenderedChanged', rendered: areaLayer })) fitToJapan();
});

// 行き先が増減したらラベルの実測もやり直す（microCMS の入稿・削除が反映されるため）
watch(
  () => props.areaMarkerLayer.dots,
  () => {
    if (isReady.value) measureAreaLabelSizes();
  },
);

onMounted(() => {
  // マップは client のみ。エラーは握りつぶさず Sentry に送るのは呼び出し側方針に委ねる。
  void initMap();
});

onBeforeUnmount(() => {
  isUnmounted = true;
  // 親（composable）はアンマウントされないため、描画状態を戻さないと再マウント後に
  // 「既に描いている」扱いのままになり、到達計測が発火せず選択もクリアされない
  props.onAreaLayerRenderedChange?.(false);
  if (boundsDebounceTimer) clearTimeout(boundsDebounceTimer);
  if (smoothZoomTimer) clearTimeout(smoothZoomTimer);
  idleListener?.remove();
  zoomListener?.remove();
  boundsListener?.remove();
  dragListener?.remove();
  clickListener?.remove();
  if (areaViewportFrame != null) cancelAnimationFrame(areaViewportFrame);
  resizeObserver?.disconnect();
  clusterer?.clearMarkers();
  markerByPinId = new Map();
});
</script>
